import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AppRoutes } from "./Routes";
import { useIdleTimer } from "react-idle-timer";
import { useSelector, useDispatch } from "react-redux";
import { useConfirmDialog } from "./hooks/useConfirmDialog";
import { logout } from "./components/api/AuthService";
import { setRefreshToken, userlogin, userlogout } from "./components/slice/authService";
import { axios } from "./lib/axios";
import { axiosSAML } from "./lib/axiosSAML";
import { apiUrl, samlApiUrl, COGNITO_CLIENT_ID, SAML_REDIRECT_URI } from "./environment";
import { triggerBackgroundSamlLogout } from "./utils/authUtils";
import { failed, success } from "./components/common/Toastify";
import { FullPageSpinner } from "./components/common/Spinner/FullPageSpinner";
import ModalPopup from "./components/apps/CTI/ModalPopup";
import moment from "moment";
import { createEncounterInbound } from "./components/apps/api/AppointmentRequests";
import { useWebSocket, WebSocketProvider } from "./ContextsAndProvider/websocketContext";
import InBoundCall from "./components/Admin/Dashboard/InBoundCall";
import { editCTIData } from "./components/api/CTI";


function AppWrapper() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const refreshTokenString = useSelector((state) => state?.auth?.refreshToken);
  const userDetails = useSelector((state) => state?.auth?.user);
  const { getConfirmation, closeConfirmation } = useConfirmDialog();
  const intervalRef = useRef(null);
  const tokenRefreshMinutes = 10;
  const inactivityMinutes = 20;
  const promptMinutes = 2;

  const location = useLocation();
  const navigate = useNavigate();
  const { lastMessage } = useWebSocket();
  const [modalData, setModalData] = useState(null);
  const hasAttemptedSAMLLogin = useRef(false);
  const [isLoading, setLoading] = useState(false);

  const idleChannel = useRef(null);
  const tabId = useRef(Date.now());
  const activeTabs = useRef(new Set());
  const [CTIModalShow, setCTIModalShow] = useState(false);
  const [inboundCallModalShow, setInboundCallModalShow] = useState(false);
  const [encounterData, setEncounterData] = useState(null);
  const [encounterLoading, setEncounterLoading] = useState(false);

  useEffect(() => {
    idleChannel.current = new BroadcastChannel("idle-sync");

    idleChannel.current.onmessage = (event) => {
      const { type, tab } = event.data;
      if (type === "ACTIVE") {
        activeTabs.current.add(tab);
      } else if (type === "IDLE") {
        activeTabs.current.delete(tab);
      } else if (type === "LOGOUT") {
        closeConfirmation();
        dispatch(userlogout());
      }
    };

    return () => {
      idleChannel.current?.close();
    };
  }, []);

  useEffect(() => {
    if(!isLoggedIn){
      closeConfirmation();
      dispatch(userlogout());
    }
  }, [isLoggedIn]);

  const markActive = () => {
    idleChannel.current?.postMessage({ type: "ACTIVE", tab: tabId.current });
  };

  const markIdle = () => {
    idleChannel.current?.postMessage({ type: "IDLE", tab: tabId.current });
  };

  useEffect(() => {
    const handleActivity = () => {
      markActive();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const samlCode = params.get("code");
    const currentPath = location.pathname;

    if (samlCode && !isLoggedIn && currentPath.includes("/login") && !hasAttemptedSAMLLogin.current) {
      hasAttemptedSAMLLogin.current = true;
      loginSAMLAttempt({
        code: samlCode,
        client_id: COGNITO_CLIENT_ID,
        grant_type: "authorization_code",
        redirect_uri: SAML_REDIRECT_URI,
      });
    }
  }, []);

  const loginAPICall = async (data) => {
    try {
      if (data) {
        const response = await axios.post(`${apiUrl}auth/admin-login`, data);
        if (response?.status === true) {
          const updatedUser = {
            ...userDetails,
            exp: response?.exp,
          };
          dispatch(userlogin(updatedUser));
          localStorage.setItem("authentication", response?.token);
        } else {
          throw new Error("Login Failed: Response status not true.");
        }
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      localStorage.clear();
      dispatch(userlogout());
      console.error("Token Refresh Error:", error);
    }
  };

  const samlLoginAPICall = async (data) => {
    try {
      setLoading(true);
      if (data) {
        const response = await axios.post(`${apiUrl}auth/admin-login-saml`, data);
        if (response?.authType === "SUCCESS") {
          setLoading(false);
          dispatch(userlogin(response.user));
          localStorage.setItem("authentication", response?.token);
          localStorage.setItem("saml_login", "true"); // Flag to track SAML login (cleared on logout)
          dispatch(setRefreshToken(response?.refresh_token));
          success("Successfully Logged In");
        } else if (response?.result?.authType === "CREATE_ACCOUNT") {
          setLoading(false);
          navigate("/saml/account-setup", { state: { data: response?.result } });
        } else {
          failed(response?.message)
          throw new Error("Login Failed: Response status not true.");
        }
        setLoading(false);
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      localStorage.clear();
      dispatch(userlogout());
      console.error("Token Refresh Error:", error);
      failed(error?.response?.data?.message || "Request failed")
      setLoading(false);
    }
  };

  const samlTokenRefresh = async (token) => {
    try {
      if (token) {
        const data = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: COGNITO_CLIENT_ID,
          refresh_token: token,
        });
        const response = await axiosSAML.post(`${samlApiUrl}/oauth2/token`, data, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
        if (response?.access_token && response?.id_token) {
          localStorage.setItem("authentication", response?.id_token);
        } else {
          failed(response?.message)
          throw new Error("Token Refresh Failed: No tokens returned.");
        }
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      localStorage.clear();
      dispatch(userlogout());
      console.error("Token Refresh Error:", error);
    }
  };

  const loginSAMLAttempt = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(`${samlApiUrl}/oauth2/token`, data, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      setLoading(false);
      if (response?.access_token) {
        samlLoginAPICall({
          access_token: response?.access_token,
          refresh_token: response?.refresh_token,
          id_token: response?.id_token,
        });
      }
    } catch (error) {
      console.error("SAML Login Error:", error);
    }
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (refreshTokenString) {
      intervalRef.current = setInterval(() => {
        if (hasAttemptedSAMLLogin.current) {
          samlTokenRefresh(refreshTokenString);
        } else {
          loginAPICall({ refresh_token: refreshTokenString });
        }
      }, 1000 * 60 * tokenRefreshMinutes);
    }
  }, [refreshTokenString]);

  const handleOnIdle = () => {
    markIdle();

    setTimeout(() => {
      if (activeTabs.current.size === 0 && isLoggedIn) {
        idleChannel.current?.postMessage({ type: "LOGOUT" });
        clearInterval(intervalRef.current);
        closeConfirmation();
        const wasSaml = logout();
        dispatch(userlogout());
        
        if (wasSaml) {
          triggerBackgroundSamlLogout();
        }
      }
    }, 500);
  };

  const handlePrompt = async () => {
    if (activeTabs.current.size > 1) {
      return;
    }

    if (isLoggedIn) {
      const confirmed = await getConfirmation({
        title: "Attention!",
        message: "You will be logged out unless you press continue.",
        actionBtnTitle: "Continue",
        showCancel: false,
      });

      if (confirmed) {
        activate();
        markActive();
      }
    } else {
      activate();
      markActive();
    }
  };

  const { activate } = useIdleTimer({
    timeout: 1000 * 60 * inactivityMinutes,
    promptBeforeIdle: 1000 * 60 * promptMinutes,
    onIdle: handleOnIdle,
    onPrompt: handlePrompt,
  });
  useEffect(() => {
    if (lastMessage && lastMessage?.callType && lastMessage?.callType?.toLowerCase() === "inbound") {
      setModalData(lastMessage);
      setCTIModalShow(true);
      
    }
  }, [lastMessage]);

  const handleProceedClick = (data) => {
    setCTIModalShow(false);
    setEncounterLoading(true);
    let encounterPayload = {
      orgId: data?.orgId,
      orgName: data?.orgName,
      serviceProvider: {
        type: "Organization",
        display: data?.orgName,
        reference: `Organization/${data?.orgId}`
      },
      requestedPeriod: {start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"), end: null},
      practitionerId: data?.practitionerId,
      practitionerName: data?.practitionerName,
      status: "in-progress-intake",
      statusHistory: [{
        status: "in-progress-intake",
        actor: {
          reference: `Practitioner/${data?.practitionerId}`,
          display: data?.practitionerName,
        },
        period: {
          start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          end: null
        }
      }],
    }

    if (data?.serviceLineId) {
      encounterPayload.serviceLineId = data?.serviceLineId;
      encounterPayload.serviceLine = {
        display: data?.serviceLineName,
        reference: `ServiceLine/${data?.serviceLineId}`,
        type: "ServiceLine"
      }
    }
    
    createEncounterInbound(encounterPayload).then((res)=>{
      if(res.status === true){
        let encounterResData = res?.data;
        editCTIData(data?.contactId, data?.orgId, {encounterId: res?.data?.id, callType: "INBOUND"}).then((res) => {
          if (res?.status === true) {
            setEncounterData(encounterResData)              
            setInboundCallModalShow(true) 
          } else {
            setEncounterData(res.data)              
            setInboundCallModalShow(true)
          }})
        
      }else{
        failed(res?.response?.data?.message || res?.response?.data?.error || res.message)
        setInboundCallModalShow(false)
      }
    }).catch((res) => {
        setInboundCallModalShow(false)
        failed(res?.response?.data?.message || res?.response?.data?.error || res.message)
      }).finally(() => setEncounterLoading(false))
  }

  const handleCancelClick = () => {
    setModalData(null);
    setCTIModalShow(false);
  }

  return (
    <div className="App">
      {isLoading && <FullPageSpinner loadingText="Signing in..." />}
      {encounterLoading && <FullPageSpinner loadingText="Loading Encounter..." />}
      {CTIModalShow && <ModalPopup data={modalData} onShow={CTIModalShow} onHide={() => handleCancelClick()} handleProceedClick={handleProceedClick}/>}
      {inboundCallModalShow && <InBoundCall encounterId={encounterData?.id} encounteredData={encounterData} modalShow={inboundCallModalShow} handleShow={() => setInboundCallModalShow(false)}/>}
      <AppRoutes />
      <ToastContainer />
    </div>
  );
}

// Wrap in Router to allow useLocation and useNavigate
export default function App() {
  return (
    <Router>
      <WebSocketProvider>
        <AppWrapper />
      </WebSocketProvider>
    </Router>
  );
}
